import {DATE_FORMATS} from '@matic/config/constants';

/**
 * Formats a date string or Date object according to the specified format
 *
 * @param date - Date to format (string, Date, or number)
 * @param format - Format to use (from DATE_FORMATS or custom)
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | number,
  format: string = DATE_FORMATS.DISPLAY
): string {
  if (!date) return '';

  const dateObject =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  // For MVP, we'll use a simple implementation
  // In a real app, consider using date-fns or similar library

  const options: Intl.DateTimeFormatOptions = {};

  if (format === DATE_FORMATS.DISPLAY) {
    options.year = 'numeric';
    options.month = 'short';
    options.day = 'numeric';
  } else if (format === DATE_FORMATS.DATETIME) {
    options.year = 'numeric';
    options.month = 'short';
    options.day = 'numeric';
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return dateObject.toLocaleDateString('en-US', options);
}

/**
 * Convert a date-only "YYYY-MM-DD" picker value into an ISO datetime
 * representing the END of that calendar day in the user's local
 * timezone, encoded as UTC.
 *
 * Why: a bare "YYYY-MM-DD" sent to the API gets interpreted as
 * "midnight (or end-of-day) UTC of that day" — which is in the past for
 * users west of UTC the moment they submit, tripping the backend's
 * ``due_date > created_at`` check constraint
 * (``ck_training_assignments_due_date_future``). Pre-computing local
 * end-of-day-in-UTC guarantees the value is in the future regardless
 * of where the user is geographically.
 *
 * Example (user in EDT picks 2026-05-05):
 *   "2026-05-05" → "2026-05-06T03:59:59.999Z"
 *   (= 23:59:59.999 EDT on 2026-05-05)
 *
 * Strings that already include a time component pass through unchanged
 * — the helper is idempotent on full ISO datetimes.
 */
export function dueDateToIsoEndOfDay(
  date: string | undefined | null
): string | undefined {
  if (!date) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, yStr, mStr, dStr] = match;
  const localEndOfDay = new Date(
    Number(yStr),
    Number(mStr) - 1,
    Number(dStr),
    23,
    59,
    59,
    999
  );
  return localEndOfDay.toISOString();
}

/**
 * Local-timezone equivalent of ``new Date(today + n days).toISOString()
 * .split('T')[0]`` — without the off-by-one bug that bites users west
 * of UTC late in the day, where ``toISOString()`` flips the date forward
 * a day. Returns "YYYY-MM-DD" suitable for ``DatePicker``'s ``minDate``
 * / ``maxDate`` props.
 */
export function localDateOffsetIso(daysFromToday: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromToday);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Formats a date string as a bare relative time phrase, with no verb prefix.
 * Buckets past a week into weeks/months/years so old dates don't render as
 * "47 days ago" — the single shared implementation for every relative-time
 * string in the app; don't reimplement this per-feature.
 *
 * @param dateString - ISO date string to format
 * @returns Relative time string (e.g., "today", "1 day ago", "3 weeks ago")
 */
export function formatRelativeTime(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffInDays <= 0) return 'today';
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`;
  return `${Math.floor(diffInDays / 365)}y ago`;
}

/**
 * Formats a date string as a relative "Assigned ..." time ago string.
 * Kept separate from `formatRelativeTime` (uncapped day count, no
 * week/month/year buckets) so existing notification call sites don't
 * silently change output as a side effect of unrelated work.
 *
 * @param dateString - ISO date string to format
 * @returns Relative time string (e.g., "Assigned today", "Assigned 1 day ago")
 */
export function formatTimeAgo(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffInDays === 0) return 'Assigned today';
  if (diffInDays === 1) return 'Assigned 1 day ago';
  return `Assigned ${diffInDays} days ago`;
}
